import { useQueries } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { workspaceItemsApi } from '../../../data/api/workspaceItems.api'
import type { WorkspaceItem } from '../../../shared/models/WorkspaceItem'
import {
  useWorkspaceItems,
  workspaceMediaItemKeys,
} from '../../../shared/queries/useWorkspaceMediaItems'

export function useWorkspaceNavigation(
  workspaceId: string,
  path: string,
  onPathChange: (path: string) => void,
) {
  const folderPath = useMemo(() => path.split('/').filter(Boolean), [path])
  const currentParentId = folderPath[folderPath.length - 1] ?? null
  const { data: items, isLoading, isError, refetch } = useWorkspaceItems(
    workspaceId,
    currentParentId,
  )

  const breadcrumbQueries = useQueries({
    queries: folderPath.map((_folderId, index) => {
      const parentId = index === 0 ? null : folderPath[index - 1]
      return {
        queryKey: workspaceMediaItemKeys.children(workspaceId, parentId),
        queryFn: () => workspaceItemsApi.list(workspaceId, parentId).then((response) => response.data),
      }
    }),
  })

  const breadcrumbs = folderPath.map((id, index) => ({
    id,
    name: breadcrumbQueries[index]?.data?.find((item) => item.id === id)?.name ?? 'Folder',
  }))

  const openFolder = useCallback(
    (item: WorkspaceItem) => {
      if (item.type === 'folder') {
        onPathChange([...folderPath, item.id].join('/'))
      }
    },
    [folderPath, onPathChange],
  )
  const navigateToRoot = useCallback(() => {
    onPathChange('')
  }, [onPathChange])
  const navigateToBreadcrumb = useCallback(
    (index: number) => {
      onPathChange(folderPath.slice(0, index + 1).join('/'))
    },
    [folderPath, onPathChange],
  )

  return {
    folderPath,
    currentParentId,
    items,
    breadcrumbs,
    isLoading,
    isError,
    refetch,
    openFolder,
    navigateToRoot,
    navigateToBreadcrumb,
  }
}
